import React, { useState } from "react";
import { Image as ImageIcon, Download, Share2, Compass, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useLang } from "@/context/LanguageContext";
import { API } from "@/lib/api";
import { canNativeShare, nativeShare, copyText, socialLinks } from "@/lib/share";
import { earnPoints } from "@/lib/usePoints";
import { trackEvent } from "@/lib/analytics";

/** Instagram-size postcard of the itinerary (PNG rendered by the backend) with download / share. */
export function TripPostcard({ code, days, lang, name, country }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const src = `${API}/og/trip/${code}.png?lang=${lang}&d=${days}&name=${encodeURIComponent(name || "")}`;
  const file = `atlas-${code}-${days}.png`;
  const blob = async () => { const r = await fetch(src); return new File([await r.blob()], file, { type: "image/png" }); };
  const download = async () => { setBusy(true); try { const f = await blob(); const u = URL.createObjectURL(f); const a = document.createElement("a"); a.href = u; a.download = file; a.click(); URL.revokeObjectURL(u); trackEvent("trip_postcard_download", { code }); } finally { setBusy(false); } };
  const share = async () => {
    const text = t("trip_share_text").replace("{country}", country).replace("{n}", days);
    const url = `${window.location.origin}/api/share/trip/${code}?d=${days}&lang=${lang}`;
    trackEvent("share", { method: "postcard", content_type: "trip_postcard", item_id: code }); earnPoints("trip_shared", lang);
    if (canNativeShare()) { try { const f = await blob(); if (navigator.canShare?.({ files: [f] })) return nativeShare({ files: [f], title: text, text: `${text} ${url}` }); } catch { /* fall through */ } return nativeShare({ title: text, text, url }); }
    window.open(socialLinks(url, text).whatsapp, "_blank", "noopener");
  };
  return (
    <>
      <button onClick={() => { setOpen(true); trackEvent("trip_postcard_open", { code }); }} className="inline-flex items-center gap-1.5 rounded-full border border-stoneLine px-3 py-1.5 text-sm hover:bg-sandDeep/50" data-testid="trip-postcard"><ImageIcon className="w-3.5 h-3.5" /> {t("postcard_trip")}</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid="trip-postcard-dialog">
          <DialogHeader><DialogTitle>{t("postcard_trip")}</DialogTitle></DialogHeader>
          <img src={src} alt="" className="w-full rounded-2xl border border-stoneLine" data-testid="trip-postcard-img" />
          <div className="flex gap-2">
            <button onClick={download} disabled={busy} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-ink text-sand px-4 py-2.5 text-sm font-semibold hover:bg-ochre disabled:opacity-60" data-testid="trip-postcard-download">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} {t("postcard_download")}</button>
            <button onClick={share} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-ochre text-white px-4 py-2.5 text-sm font-semibold hover:bg-ink" data-testid="trip-postcard-share"><Share2 className="w-4 h-4" /> {t("trip_share")}</button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** "Guess my destination" challenge link (the share page hides the country). */
export function GuessChallenge({ code, days, lang, name = "" }) {
  const { t } = useLang();
  const share = async () => {
    const url = `${window.location.origin}/api/share/guess/${code}?d=${days}&lang=${lang}${name ? `&by=${encodeURIComponent(name)}` : ""}`;
    const text = t("guess_share_text");
    trackEvent("share", { method: canNativeShare() ? "native" : "copy", content_type: "trip_guess", item_id: code }); earnPoints("trip_shared", lang);
    if (canNativeShare()) return nativeShare({ title: text, text, url });
    await copyText(`${text} ${url}`); toast.success(t("copied"));
  };
  return <button onClick={share} className="inline-flex items-center gap-1.5 rounded-full bg-[#5B4B8A] text-white px-3 py-1.5 text-sm font-semibold hover:bg-ink transition-colors" data-testid="trip-guess-share"><Compass className="w-3.5 h-3.5" /> {t("guess_challenge")}</button>;
}
